import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, FieldError } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { NationalNumber } from 'libphonenumber-js';

import {
  BodyText,
  Div,
  Dropdown,
  Gutter,
  Input,
  PhoneNumberInput,
  OptionListOption,
  CountyCodeShape,
  DatePicker,
} from '@gaiads/telia-react-component-library';
import { PopupModal, PopupModalTranslations, VERIFY_PHONE_NUMBER_PATTERN } from '@cdaas/reusable-components-daas';

import { COUNTRY_CALLING_CODES, VERIFY_EMAIL_PATTERN } from '../../../../../utils/constants';
import { parsingHelpers, sanitizeEmployeeId } from '../../../../../utils';
import { RootState } from '../../../../../store/reducers';
import { UserWithPossibleError } from '../../utils';

export interface UserEditModalProps {
  user: UserWithPossibleError | null;
  saveUserCallback: (data: {
    id: string;
    email: string;
    phoneNumber: string;
    userGroup: string;
    employeeId: string | null;
    preScheduledOnboardingAt: string | null;
  }) => void;
  modalOpen: boolean;
  closeCallback: () => void;
}

interface FormType {
  email: string;
  phoneNumber: string | NationalNumber;
  userGroup: string;
  countryCallingCode: string | undefined;
  employeeId: string | undefined;
  preScheduledOnboardingAt: string | undefined;
}

const UserEditModal: FunctionComponent<UserEditModalProps> = ({ user, saveUserCallback, modalOpen, closeCallback }) => {
  const { t, i18n } = useTranslation('editOrganization');

  const userGroups = useSelector((state: RootState) => state.userGroups.userGroups);

  const phoneNumber = parsingHelpers.extractPartsFromFullPhoneNumber(user?.phone_number || '');

  const [selectedCountryCode, setSelectedCountryCode] = useState<CountyCodeShape | undefined>(
    COUNTRY_CALLING_CODES.find(ccc => ccc.code === phoneNumber?.countryCallingCode),
  );
  const [selectedUserGroup, setSelectedUserGroup] = useState<OptionListOption>({
    label: user?.user_group?.name || '',
    value: user?.user_group?.id || '',
  });

  const userGroupOptions: OptionListOption[] = userGroups.map(ug => ({ label: ug.name, value: ug.id! }));

  const getDefaultFormValues = (user: UserWithPossibleError | null) => {
    const phoneNumber = parsingHelpers.extractPartsFromFullPhoneNumber(user?.phone_number ?? '');
    return {
      email: user?.email ?? '',
      phoneNumber: COUNTRY_CALLING_CODES.some(ccc => phoneNumber?.countryCallingCode === ccc.code)
        ? phoneNumber?.nationalNumber
        : (user?.phone_number ?? ''),
      userGroup: user?.user_group?.id ?? '',
      countryCallingCode: COUNTRY_CALLING_CODES.some(ccc => phoneNumber?.countryCallingCode === ccc.code)
        ? phoneNumber?.countryCallingCode + ''
        : undefined,
      employeeId: !user || user.employee_id === null ? '' : user.employee_id,
      preScheduledOnboardingAt: !user || user.prescheduled_onboarding_at === null ? '' : user.prescheduled_onboarding_at,
    };
  };

  const {
    handleSubmit,
    control,
    setValue,
    register,
    formState: { errors },
    trigger,
    reset,
    clearErrors,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: getDefaultFormValues(user),
  });

  register('userGroup', { required: `${t('usersAdmin.importUsers.reviewAndSave.errors.noUserGroupSelected')}` });

  register('countryCallingCode', {
    validate: value => {
      return !COUNTRY_CALLING_CODES.find(ccc => value === ccc.code)
        ? `${t('usersAdmin.importUsers.reviewAndSave.errors.countryCallingCodeMissing')}`
        : true;
    },
  });

  const isValidPreScheduledOnboardingDate = (onboardingDate: string | null | undefined): boolean => {
    if (!onboardingDate) return true;

    const date = new Date(onboardingDate);
    return !Number.isNaN(date.getTime()) && date.setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
  };

  register('preScheduledOnboardingAt', {
    validate: value => {
      return isValidPreScheduledOnboardingDate(value);
    },
  });

  useEffect(() => {
    reset(getDefaultFormValues(user));
    const phoneNumber = parsingHelpers.extractPartsFromFullPhoneNumber(user?.phone_number || '');
    setSelectedCountryCode(COUNTRY_CALLING_CODES.find(ccc => ccc.code === phoneNumber?.countryCallingCode));
    setSelectedUserGroup({ label: user?.user_group?.name || '', value: user?.user_group?.id || '' });
  }, [user, reset]);

  useEffect(() => {
    trigger();
  }, [selectedUserGroup, trigger]);

  const handleUserGroupChange = (_event: SyntheticEvent<HTMLDivElement>, option: OptionListOption): void => {
    setValue('userGroup', option.value);
    setSelectedUserGroup(option);
    clearErrors('userGroup');
  };

  const handleCountryCodeChange = (_event: SyntheticEvent<HTMLDivElement>, option: CountyCodeShape | undefined): void => {
    if (!option) return;
    setValue('countryCallingCode', option.code);
    setSelectedCountryCode(option);
    clearErrors('countryCallingCode');
  };

  const formatDateToYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleUserSave = () => {
    handleSubmit(data => {
      saveUserCallback({
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        id: user?.id!,
        email: data.email,
        phoneNumber: '+' + data.countryCallingCode + data.phoneNumber,
        userGroup: data.userGroup,
        employeeId: sanitizeEmployeeId(data.employeeId),
        preScheduledOnboardingAt: data.preScheduledOnboardingAt ? formatDateToYYYYMMDD(new Date(data.preScheduledOnboardingAt)) : null,
      });
    })();
  };

  const popupModalTranslations: PopupModalTranslations = {
    heading: t('usersAdmin.importUsers.reviewAndSave.userEditModal.title'),
    actionButton: t('usersAdmin.importUsers.reviewAndSave.userEditModal.saveButton'),
    closeButton: t('usersAdmin.importUsers.reviewAndSave.userEditModal.cancelButton'),
  };

  return (
    <PopupModal
      translations={popupModalTranslations}
      onAction={handleUserSave}
      onClose={closeCallback}
      actionButtonDisabled={
        !!errors.countryCallingCode || !!errors.email || !!errors.phoneNumber || !!errors.userGroup || !!errors.preScheduledOnboardingAt
      }
      hidden={!modalOpen}
    >
      <>
        <Div style={{ maxWidth: '256px' }}>
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label={t('usersAdmin.importUsers.reviewAndSave.userEditModal.emailInputLabel')}
                onChange={onChange}
                value={value}
                errorText={errors.email?.message}
              />
            )}
            rules={{
              required: `${t('usersAdmin.importUsers.reviewAndSave.errors.emailMissing')}`,
              pattern: {
                value: VERIFY_EMAIL_PATTERN,
                message: t('usersAdmin.importUsers.reviewAndSave.errors.emailInvalid'),
              },
            }}
          />
        </Div>
        <Gutter size="lg" />
        <Div style={{ maxWidth: '256px' }}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, value } }) => (
              <PhoneNumberInput
                label={t('usersAdmin.importUsers.reviewAndSave.userEditModal.phoneNumberInputLabel')}
                onCountryCodeChange={handleCountryCodeChange}
                onNumberChange={onChange}
                numberValue={value as string}
                countryCodes={COUNTRY_CALLING_CODES}
                selectedCountryCode={selectedCountryCode}
                errorText={errors.countryCallingCode?.message || (errors.phoneNumber as FieldError)?.message}
              />
            )}
            rules={{
              required: `${t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberMissing')}`,
              pattern: {
                value: VERIFY_PHONE_NUMBER_PATTERN,
                message: t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberInvalid'),
              },
            }}
          />
        </Div>
        <Gutter size="lg" />
        <Div style={{ maxWidth: '256px' }}>
          <Dropdown
            label={t('usersAdmin.importUsers.reviewAndSave.userEditModal.userGroupInputLabel')}
            onChange={handleUserGroupChange}
            selectedOption={selectedUserGroup}
            options={userGroupOptions}
            errorText={errors.userGroup?.message}
          />
        </Div>
        <Gutter size="lg" />
        <Div style={{ maxWidth: '256px' }}>
          <Controller
            name="employeeId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input label={t('usersAdmin.importUsers.reviewAndSave.userEditModal.employeeIdInput')} onChange={onChange} value={value} />
            )}
          />
        </Div>
        <Gutter size="lg" />
        <Div style={{ maxWidth: '384px' }}>
          <Controller
            name="preScheduledOnboardingAt"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                format="DD.MM.YYYY"
                placeholderFormat={'DD.MM.YYYY'}
                locale={i18n.language}
                label={t('usersAdmin.importUsers.reviewAndSave.userEditModal.preScheduledOnboardingDateInput')}
                onChange={(selectedDate: Date) => {
                  onChange(selectedDate);
                }}
                value={(() => {
                  if (!value) return undefined;
                  const date = new Date(value);
                  return Number.isNaN(date.getTime())
                    ? t('usersAdmin.importUsers.reviewAndSave.userEditModal.preScheduledOnboardingDateInvalid')
                    : date;
                })()}
                errorText={errors.preScheduledOnboardingAt?.message ?? ''}
                dayPickerProps={{ disabledDays: { before: new Date(new Date().setHours(0, 0, 0, 0)) } }}
              />
            )}
            rules={{
              validate: value => {
                if (!isValidPreScheduledOnboardingDate(value)) {
                  return t('usersAdmin.importUsers.reviewAndSave.userEditModal.preScheduledOnboardingDateInvalid');
                }
                return true;
              },
            }}
          />
          {/* Needs to be added separately because helperText in DatePicker does not have enough space: */}
          <BodyText size="sm" color="gray500" margin={{ top: 'xs' }} style={{ lineHeight: 1.5 }}>
            {t('usersAdmin.importUsers.reviewAndSave.userEditModal.preScheduledOnboardingHelperText')}
          </BodyText>
        </Div>
        <Gutter size="layout-9" />
      </>
    </PopupModal>
  );
};

export default UserEditModal;
